import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ContractMessage } from '../../../../types/ContractMessage';
import { Button } from '../../../cdl/Button/Button';
import { DropdownMenu } from '../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { DeleteConfirmPopup } from '../../../common/DeleteConfirmPopup/DeleteConfirmPopup';
import { translate } from '../../../common/helpers/translate.helper';
import { IconDots } from '../../../common/icons/cdl/Dots';
import { ContractDeactivateConfirmModal } from '../../../contracts/common/components/ContractDeactivateConfirmModal';
import { useContractActions } from '../../../contracts/common/hooks/useContractActions';
import { useContractDelete } from '../../../contracts/overview/hooks/useContractDelete';

interface ContractDetailActionsProps {
    contract: ContractMessage;
}

export const ContractDetailActions = ({ contract }: ContractDetailActionsProps) => {
    const navigate = useNavigate();
    const { addToast, addErrorToast } = useToasts();
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const deleteMutation = useContractDelete();
    const {
        options,
        openEditDrawer,
        prefetchDraftAwardValidation,
        isDeactivateConfirmOpen,
        closeDeactivateConfirm,
        deactivateContract,
        isDeactivating,
    } = useContractActions({
        contract,
        onDelete: () => setIsDeleteConfirmOpen(true),
    });

    const deleteContract = () => {
        deleteMutation.mutate(contract.id, {
            onSuccess: () => {
                addToast(translate('contracts.deleteSuccessToast'));
                setIsDeleteConfirmOpen(false);
                navigate({ to: '/contracts' });
            },
            onError: () => {
                addErrorToast(translate('contracts.deleteErrorToast'));
            },
        });
    };

    return (
        <>
            <ButtonGroup>
                <Button onClick={openEditDrawer}>{translate('contracts.edit')}</Button>
                {options.length > 0 ? (
                    <DropdownMenu
                        align="end"
                        options={options}
                        trigger={
                            <IconButton
                                emphasis="medium"
                                Icon={IconDots}
                                onMouseEnter={prefetchDraftAwardValidation}
                                onFocus={prefetchDraftAwardValidation}
                                onPointerDown={prefetchDraftAwardValidation}
                            />
                        }
                    />
                ) : null}
            </ButtonGroup>

            <DeleteConfirmPopup
                isOpen={isDeleteConfirmOpen}
                onSubmit={deleteContract}
                onDismiss={() => setIsDeleteConfirmOpen(false)}
                headline={translate('contracts.deleteConfirmTitle')}
                text={translate('contracts.deleteConfirmDescription')}
                itemToDelete={contract.name}
                isSubmitting={deleteMutation.isPending}
            />

            <ContractDeactivateConfirmModal
                isOpen={isDeactivateConfirmOpen}
                onDismiss={closeDeactivateConfirm}
                onConfirm={deactivateContract}
                isSubmitting={isDeactivating}
            />
        </>
    );
};
